import { verifyPasskeyApproval } from './approval.js';
import { copy, fromBase64Url, fromUtf8, toBase64Url, utf8, } from './bytes.js';
import { exactKeys, hkdf256, importAccountDataKey, importAesKey, openBox, positiveInteger, sealBox, validId, } from './content.js';
import { domainSeparated, hashCanonical } from './hash.js';
import { canonicalize } from './jcs.js';
import { accountKeyCommitment } from './key-directory.js';
import { exportP256PublicJwk, jwkThumbprint } from './keys.js';
import { generateAgreementKeyPair, importAgreementPublicKey, unwrapAccountKey, } from './wrapping.js';
const subtle = () => globalThis.crypto.subtle;
export const KEY_DOMAIN = {
    epoch: 'viko/key-epoch/v1',
    recipient: 'viko/content-recipient/v1',
    wrapHkdf: 'viko/key-wrap/hkdf/v1',
    wrapAad: 'viko/key-wrap/aad/v1',
    escrowAad: 'viko/key-escrow/aad/v1',
    daemonContentKey: 'viko/daemon-content-key/v1',
};
// ---------------------------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------------------------
const RECIPIENT_TYPES = ['device', 'daemon', 'recovery', 'prf'];
export async function validateRecipient(r) {
    if (!r || typeof r !== 'object')
        throw new Error('invalid content recipient');
    exactKeys(r, r.ref === undefined ? ['id', 'type', 'publicKey'] : ['id', 'type', 'publicKey', 'ref']);
    if (!RECIPIENT_TYPES.includes(r.type))
        throw new Error('invalid content recipient type');
    if (r.ref !== undefined)
        validId(r.ref);
    await importAgreementPublicKey(r.publicKey);
    if ((await jwkThumbprint(r.publicKey)) !== r.id)
        throw new Error('recipient id mismatch');
}
function validB64(value, bytes) {
    if (typeof value !== 'string' || fromBase64Url(value).length !== bytes)
        throw new Error('invalid base64url field');
}
export async function recipientFor(type, publicKey, ref) {
    const pk = { kty: 'EC', crv: 'P-256', x: publicKey.x, y: publicKey.y };
    const r = { id: await jwkThumbprint(pk), type, publicKey: pk };
    if (ref !== undefined)
        r.ref = ref;
    await validateRecipient(r);
    return r;
}
async function validateEpochStatement(s, accountId) {
    exactKeys(s, ['v', 'type', 'accountId', 'epoch', 'keyCommitment', 'recipients', 'issuedAt']);
    if (s.v !== 1 || s.type !== 'viko.key-epoch')
        throw new Error('unsupported key epoch');
    if (s.accountId !== accountId)
        throw new Error('wrong key epoch account');
    positiveInteger(s.epoch);
    validB64(s.keyCommitment, 32);
    if (!Number.isSafeInteger(s.issuedAt) || s.issuedAt < 0)
        throw new Error('invalid key epoch time');
    if (!Array.isArray(s.recipients) || s.recipients.length < 1 || s.recipients.length > 1024)
        throw new Error('invalid key epoch recipients');
    const ids = new Set();
    for (const r of s.recipients) {
        await validateRecipient(r);
        if (ids.has(r.id))
            throw new Error('duplicate recipient');
        ids.add(r.id);
    }
}
// ---------------------------------------------------------------------------------------------
// Passkey-signed statements
// ---------------------------------------------------------------------------------------------
export function createKeyEpochChallenge(statement) {
    return hashCanonical(KEY_DOMAIN.epoch, statement);
}
export function createContentRecipientChallenge(statement) {
    return hashCanonical(KEY_DOMAIN.recipient, statement);
}
export async function createKeyEpochStatement(input) {
    const s = {
        v: 1,
        type: 'viko.key-epoch',
        accountId: input.accountId,
        epoch: input.epoch,
        keyCommitment: await accountKeyCommitment(input.adk, input.accountId, input.epoch),
        recipients: structuredClone(input.recipients),
        issuedAt: input.now ?? Date.now(),
    };
    await validateEpochStatement(s, input.accountId);
    return s;
}
/** Verify a key epoch against the endpoint's own pinned passkeys (never a server-provided set). */
export async function verifyKeyEpoch(signed, accountId, trust) {
    const copyOf = structuredClone(signed);
    exactKeys(copyOf, ['statement', 'assertion']);
    await validateEpochStatement(copyOf.statement, accountId);
    const r = await verifyPasskeyApproval(copyOf.assertion, await createKeyEpochChallenge(copyOf.statement), trust);
    if (!r.ok)
        throw new Error(`key epoch not approved: ${r.reason}`);
    return copyOf.statement;
}
export async function verifyContentRecipient(signed, accountId, trust) {
    const copyOf = structuredClone(signed);
    exactKeys(copyOf, ['statement', 'assertion']);
    const s = copyOf.statement;
    exactKeys(s, ['v', 'type', 'accountId', 'recipient', 'issuedAt']);
    if (s.v !== 1 || s.type !== 'viko.content-recipient' || s.accountId !== accountId)
        throw new Error('invalid content recipient statement');
    if (!Number.isSafeInteger(s.issuedAt))
        throw new Error('invalid content recipient time');
    await validateRecipient(s.recipient);
    const r = await verifyPasskeyApproval(copyOf.assertion, await createContentRecipientChallenge(s), trust);
    if (!r.ok)
        throw new Error(`content recipient not approved: ${r.reason}`);
    return s.recipient;
}
export function daemonContentKeyBinding(daemonKeyId, publicKey) {
    validId(daemonKeyId);
    return {
        v: 1,
        type: 'viko.daemon-content-key',
        daemonKeyId,
        publicKey: { kty: 'EC', crv: 'P-256', x: publicKey.x, y: publicKey.y },
    };
}
// ---------------------------------------------------------------------------------------------
// Wraps: all epochs a recipient may hold, sealed to its ECDH key
// ---------------------------------------------------------------------------------------------
function wrapAad(accountId, recipientId, ephemeral) {
    validId(accountId);
    validId(recipientId);
    return domainSeparated(KEY_DOMAIN.wrapAad, { v: 1, accountId, recipientId, ephemeral });
}
async function kek(privateKey, pub, aad) {
    const key = await importAgreementPublicKey(pub);
    const shared = new Uint8Array(await subtle().deriveBits({ name: 'ECDH', public: key }, privateKey, 256));
    try {
        const raw = await hkdf256(shared, utf8(KEY_DOMAIN.wrapHkdf), aad);
        try {
            return await importAesKey(raw);
        }
        finally {
            raw.fill(0);
        }
    }
    finally {
        shared.fill(0);
    }
}
function encodeEpochs(epochs) {
    if (!epochs.length || epochs.length > 4096)
        throw new Error('invalid epoch set');
    const seen = new Set();
    const list = epochs.map((e) => {
        positiveInteger(e.epoch);
        if (e.adk.length !== 32)
            throw new Error('ADK must be 256 bits');
        if (seen.has(e.epoch))
            throw new Error('duplicate epoch');
        seen.add(e.epoch);
        return { epoch: e.epoch, adk: toBase64Url(e.adk) };
    });
    return utf8(canonicalize({ v: 1, epochs: list }));
}
function decodeEpochs(bytes) {
    const parsed = JSON.parse(fromUtf8(bytes));
    exactKeys(parsed, ['v', 'epochs']);
    if (parsed.v !== 1 ||
        !Array.isArray(parsed.epochs) ||
        !parsed.epochs.length ||
        parsed.epochs.length > 4096)
        throw new Error('invalid key wrap');
    const seen = new Set();
    return parsed.epochs.map((e) => {
        exactKeys(e, ['epoch', 'adk']);
        positiveInteger(e.epoch);
        if (seen.has(e.epoch))
            throw new Error('duplicate epoch');
        seen.add(e.epoch);
        const adk = fromBase64Url(e.adk);
        if (adk.length !== 32)
            throw new Error('invalid wrapped ADK');
        return { epoch: e.epoch, adk };
    });
}
/** Caller MUST have authorized `recipient` through a verified passkey-signed statement. */
export async function sealKeysTo(accountId, recipient, epochs) {
    await validateRecipient(recipient);
    const pair = await generateAgreementKeyPair();
    const ephemeral = await exportP256PublicJwk(pair.publicKey);
    const aad = wrapAad(accountId, recipient.id, ephemeral);
    const key = await kek(pair.privateKey, recipient.publicKey, aad);
    const plain = encodeEpochs(epochs);
    try {
        return {
            v: 1,
            accountId,
            recipientId: recipient.id,
            ephemeral,
            box: await sealBox(key, aad, plain),
        };
    }
    finally {
        plain.fill(0);
    }
}
/** Unauthenticated sender: check every returned key with `checkEpochKeys` before installing. */
export async function openKeysFrom(wrap, own) {
    exactKeys(wrap, ['v', 'accountId', 'recipientId', 'ephemeral', 'box']);
    if (wrap.v !== 1 || wrap.accountId !== own.accountId)
        throw new Error('wrong key wrap account');
    if (wrap.recipientId !== (await jwkThumbprint(own.publicKey)))
        throw new Error('wrap recipient mismatch');
    const aad = wrapAad(own.accountId, wrap.recipientId, wrap.ephemeral);
    const key = await kek(own.privateKey, wrap.ephemeral, aad);
    const plain = await openBox(key, aad, wrap.box);
    try {
        return decodeEpochs(plain);
    }
    finally {
        plain.fill(0);
    }
}
/** Validate the complete set before checking or importing any epoch. */
function validateEpochKeys(keys) {
    if (!Array.isArray(keys) || !keys.length || keys.length > 4096)
        throw new Error('invalid epoch set');
    const seen = new Set();
    for (const key of keys) {
        positiveInteger(key.epoch);
        if (!(key.adk instanceof Uint8Array) || key.adk.length !== 32)
            throw new Error('invalid ADK');
        if (seen.has(key.epoch))
            throw new Error('duplicate epoch');
        seen.add(key.epoch);
    }
}
/**
 * The install gate: every key must match an authenticated commitment for its epoch.
 * `commitments` comes from verified passkey-signed statements only.
 */
export async function checkEpochKeys(accountId, keys, commitments) {
    validateEpochKeys(keys);
    validId(accountId);
    for (const k of keys) {
        const expected = commitments.get(k.epoch);
        if (!expected)
            throw new Error(`no approved commitment for epoch ${k.epoch}`);
        if ((await accountKeyCommitment(k.adk, accountId, k.epoch)) !== expected)
            throw new Error('ADK commitment mismatch');
    }
}
/** Low-level import only: caller MUST first authenticate commitments with checkEpochKeys. */
export async function importEpochKeys(keys) {
    validateEpochKeys(keys);
    const out = new Map();
    for (const k of keys)
        out.set(k.epoch, await importAccountDataKey(k.adk));
    return out;
}
export function wipeEpochKeys(keys) {
    for (const k of keys)
        k.adk.fill(0);
}
// ---------------------------------------------------------------------------------------------
// Escrow: a recipient private key sealed under a PRF- or recovery-derived KEK
// ---------------------------------------------------------------------------------------------
function escrowAad(e) {
    validId(e.accountId);
    validId(e.recipientId);
    if (e.kind === 'prf')
        validId(e.credentialId ?? '');
    else if (e.kind !== 'recovery' || e.credentialId !== undefined)
        throw new Error('invalid escrow');
    return domainSeparated(KEY_DOMAIN.escrowAad, e);
}
/** A fresh extractable-once ECDH pair for an escrowed recipient (recovery / PRF). */
export async function createEscrowRecipient(type, ref) {
    const pair = await generateAgreementKeyPair({ extractable: true });
    const pkcs8 = new Uint8Array(await subtle().exportKey('pkcs8', pair.privateKey));
    return {
        recipient: await recipientFor(type, await exportP256PublicJwk(pair.publicKey), ref),
        pkcs8,
    };
}
export async function sealEscrow(kekKey, header, contents) {
    const h = { v: 1, ...header };
    const plain = utf8(canonicalize({
        v: 1,
        pkcs8: toBase64Url(contents.pkcs8),
        credentials: contents.credentials,
        rpId: contents.rpId,
    }));
    try {
        return { ...h, box: await sealBox(kekKey, escrowAad(h), plain) };
    }
    finally {
        plain.fill(0);
    }
}
export async function openEscrow(kekKey, escrow, expected) {
    const { box, ...header } = escrow;
    exactKeys(escrow, header.credentialId === undefined
        ? ['v', 'accountId', 'kind', 'recipientId', 'box']
        : ['v', 'accountId', 'kind', 'recipientId', 'credentialId', 'box']);
    if (escrow.v !== 1 || escrow.accountId !== expected.accountId || escrow.kind !== expected.kind)
        throw new Error('wrong escrow');
    const plain = await openBox(kekKey, escrowAad(header), box);
    let pkcs8;
    try {
        const parsed = JSON.parse(fromUtf8(plain));
        exactKeys(parsed, ['v', 'pkcs8', 'credentials', 'rpId']);
        if (parsed.v !== 1 || !Array.isArray(parsed.credentials) || typeof parsed.rpId !== 'string')
            throw new Error('invalid escrow contents');
        pkcs8 = fromBase64Url(parsed.pkcs8);
        const exportable = await subtle().importKey('pkcs8', copy(pkcs8), { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
        const publicKey = await exportP256PublicJwk(exportable);
        if ((await jwkThumbprint(publicKey)) !== escrow.recipientId)
            throw new Error('escrow key mismatch');
        const privateKey = await subtle().importKey('pkcs8', copy(pkcs8), { name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
        return {
            contents: { pkcs8: copy(pkcs8), credentials: parsed.credentials, rpId: parsed.rpId },
            privateKey,
            publicKey,
        };
    }
    finally {
        plain.fill(0);
        pkcs8?.fill(0);
    }
}
export async function createDirectoryEscrow(input) {
    validId(input.accountId);
    if (input.kind === 'prf')
        validId(input.credentialId ?? '');
    else if (input.credentialId !== undefined || input.prfSalt !== undefined)
        throw new Error('invalid recovery escrow');
    if (input.prfSalt !== undefined && input.prfSalt !== 2)
        throw new Error('invalid PRF salt');
    const { recipient, pkcs8 } = await createEscrowRecipient(input.kind);
    try {
        const escrow = await sealEscrow(input.kek, {
            accountId: input.accountId,
            kind: input.kind,
            recipientId: recipient.id,
            ...(input.kind === 'prf' ? { credentialId: input.credentialId } : {}),
        }, { pkcs8, credentials: structuredClone(input.credentials), rpId: input.rpId });
        const recipientId = input.kind === 'prf' ? input.credentialId : recipient.id;
        return {
            record: {
                version: 1,
                accountId: input.accountId,
                kind: input.kind,
                recipientId,
                publicKey: recipient.publicKey,
                escrow,
                ...(input.prfSalt ? { prfSalt: input.prfSalt } : {}),
            },
            recipient: { id: recipientId, type: input.kind, keyId: recipient.id },
        };
    }
    finally {
        pkcs8.fill(0);
    }
}
/** Wrong code/PRF, swapped public key or account/kind substitution all fail closed. */
export async function openDirectoryEscrow(record, kekKey, expected) {
    const r = structuredClone(record);
    exactKeys(r, r.prfSalt === undefined
        ? ['version', 'accountId', 'kind', 'recipientId', 'publicKey', 'escrow']
        : ['version', 'accountId', 'kind', 'recipientId', 'publicKey', 'escrow', 'prfSalt']);
    if (r.version !== 1 || r.accountId !== expected.accountId || r.kind !== expected.kind)
        throw new Error('wrong escrow');
    if (r.prfSalt !== undefined && (r.prfSalt !== 2 || r.kind !== 'prf'))
        throw new Error('invalid PRF salt');
    await importAgreementPublicKey(r.publicKey);
    const keyId = await jwkThumbprint(r.publicKey);
    if (r.escrow.recipientId !== keyId)
        throw new Error('escrow key mismatch');
    if (r.kind === 'prf'
        ? r.recipientId !== r.escrow.credentialId ||
            (expected.credentialId !== undefined && expected.credentialId !== r.recipientId)
        : r.recipientId !== keyId)
        throw new Error('escrow recipient mismatch');
    const opened = await openEscrow(kekKey, r.escrow, { accountId: r.accountId, kind: r.kind });
    try {
        if ((await jwkThumbprint(opened.publicKey)) !== keyId)
            throw new Error('escrow key mismatch');
        return {
            recipientId: r.recipientId,
            keyId,
            privateKey: opened.privateKey,
            publicKey: opened.publicKey,
            credentials: opened.contents.credentials,
            rpId: opened.contents.rpId,
        };
    }
    finally {
        opened.contents.pkcs8.fill(0);
    }
}
/**
 * Unwrap every escrowed epoch and install-gate it against authenticated directory commitments.
 * Caller zeroes the returned raw keys (`wipeEpochKeys`) after importing/rewrapping them.
 */
export async function openEscrowEpochKeys(accountId, opened, wraps, commitments) {
    const keys = [];
    try {
        for (const { epoch, wrap } of wraps) {
            const adk = await unwrapAccountKey(wrap, { version: 1, accountId, epoch, recipientId: opened.recipientId }, opened.privateKey, opened.publicKey);
            keys.push({ epoch, adk });
        }
        await checkEpochKeys(accountId, keys, commitments);
        return keys;
    }
    catch (e) {
        wipeEpochKeys(keys);
        throw e;
    }
}
//# sourceMappingURL=account-keys.js.map